import React from "react";

/* Card 1 illustration: AI advancement vs Curriculum gap chart */
const CurriculumChart = () => (
  <div className="h-[280px] relative p-4">
    <svg viewBox="0 0 420 200" className="w-full h-full" fill="none">
      {/* Grid lines */}
      <line x1="50" y1="180" x2="380" y2="180" stroke="#E5E7EB" strokeWidth="1" />

      {/* Axis labels */}
      <text x="370" y="198" fontSize="10" fill="#9CA3AF" textAnchor="end">Curriculum</text>
      <text x="370" y="208" fontSize="10" fill="#9CA3AF" textAnchor="end">Updates</text>

      {/* Purple curriculum line (slow) */}
      <path d="M60,165 Q150,150 220,130 Q300,105 370,90" stroke="#8B5CF6" strokeWidth="2" fill="none" strokeDasharray="6 3" opacity="0.5" />
      {/* Purple filled area under curriculum line */}
      <path d="M60,165 Q150,150 220,130 Q300,105 370,90 L370,180 L60,180 Z" fill="#8B5CF6" opacity="0.06" />

      {/* Green AI advancement line (steep) */}
      <path d="M60,155 Q120,140 180,110 Q250,60 320,30 L370,15" stroke="#10B981" strokeWidth="2.5" fill="none" />
      {/* Arrow at end of green line */}
      <polygon points="370,15 360,22 362,14" fill="#10B981" />

      {/* Gap bracket on right */}
      <line x1="385" y1="15" x2="385" y2="90" stroke="#6B7280" strokeWidth="1" strokeDasharray="3" />
      <text x="400" y="55" fontSize="12" fill="#6B7280" fontWeight="600">Gap</text>

      {/* AI Advancement label */}
      <text x="330" y="48" fontSize="9" fill="#6B7280">AI Advancement</text>

      {/* Data points with labels */}
      {/* GPT-3 */}
      <rect x="70" y="147" width="32" height="18" rx="4" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1" />
      <text x="76" y="159" fontSize="9" fill="#10B981" fontWeight="600">22</text>
      <text x="108" y="159" fontSize="9" fill="#6B7280">GPT-3</text>

      {/* GPT-4 */}
      <rect x="190" y="100" width="32" height="18" rx="4" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1" />
      <text x="196" y="112" fontSize="9" fill="#10B981" fontWeight="600">23</text>
      <text x="228" y="112" fontSize="9" fill="#6B7280">GPT-4</text>

      {/* Gemini */}
      <rect x="300" y="22" width="32" height="18" rx="4" fill="#10B981" opacity="0.15" stroke="#10B981" strokeWidth="1" />
      <text x="306" y="34" fontSize="9" fill="#10B981" fontWeight="600">24</text>
      <text x="338" y="34" fontSize="9" fill="#6B7280">Gemini</text>

      {/* Green dots on line */}
      <circle cx="80" cy="155" r="4" fill="#10B981" />
      <circle cx="200" cy="108" r="4" fill="#10B981" />
      <circle cx="315" cy="30" r="4" fill="#10B981" />
    </svg>
  </div>
);

/* Card 2 illustration: World map with faculty avatars */
const WorldMapChart = () => (
  <div className="h-[280px] relative p-4 overflow-hidden">
    {/* Dot map background */}
    <div className="absolute inset-0 opacity-20">
      <svg viewBox="0 0 400 200" className="w-full h-full">
        {/* Simplified world map dots */}
        {Array.from({ length: 40 }, (_, row) =>
          Array.from({ length: 80 }, (_, col) => {
            const x = col * 5 + 10;
            const y = row * 5 + 5;
            // Create landmass shapes
            const isLand =
              (x > 60 && x < 180 && y > 30 && y < 120) || // Europe/Africa
              (x > 200 && x < 350 && y > 20 && y < 130) || // Asia
              (x > 20 && x < 120 && y > 40 && y < 90) ||   // Americas
              (x > 250 && x < 340 && y > 140 && y < 185);   // Oceania
            if (!isLand || Math.random() > 0.4) return null;
            return <circle key={`${row}-${col}`} cx={x} cy={y} r="1.2" fill="#9CA3AF" />;
          })
        )}
      </svg>
    </div>
    {/* Avatar pins */}
    <div className="absolute top-[15%] left-[25%] w-8 h-8 rounded-full bg-gray-200 border-2 border-white shadow overflow-hidden">
      <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=face" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute top-[35%] left-[55%] w-9 h-9 rounded-full bg-gray-200 border-2 border-white shadow overflow-hidden">
      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute top-[55%] left-[30%] w-8 h-8 rounded-full bg-gray-200 border-2 border-white shadow overflow-hidden">
      <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute top-[65%] right-[15%] w-8 h-8 rounded-full bg-gray-200 border-2 border-white shadow overflow-hidden">
      <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="absolute top-[10%] right-[30%] w-7 h-7 rounded-full bg-gray-200 border-2 border-white shadow overflow-hidden">
      <img src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=64&h=64&fit=crop&crop=face" alt="" className="w-full h-full object-cover" />
    </div>
  </div>
);

/* Card 4 illustration: ROI vs Cost bar chart */
const CostChart = () => (
  <div className="h-[280px] relative p-4">
    <svg viewBox="0 0 420 210" className="w-full h-full" fill="none">
      {/* Legend */}
      <text x="20" y="18" fontSize="11" fill="#1a1a2e" fontWeight="700">Last month</text>
      <rect x="20" y="28" width="10" height="10" rx="2" fill="#EF4444" />
      <text x="35" y="37" fontSize="9" fill="#6B7280">ROI</text>
      <rect x="70" y="28" width="10" height="10" rx="2" fill="#8B5CF6" />
      <text x="85" y="37" fontSize="9" fill="#6B7280">Avg. Cost per month</text>

      {/* Bars */}
      {[
        { x: 30, h: 80, color: "#E5E7EB" },
        { x: 70, h: 95, color: "#E5E7EB" },
        { x: 110, h: 70, color: "#E5E7EB" },
        { x: 150, h: 100, color: "#E5E7EB" },
        { x: 190, h: 90, color: "#E5E7EB" },
        { x: 230, h: 150, color: "#8B5CF6" },
        { x: 270, h: 85, color: "#E5E7EB" },
        { x: 310, h: 95, color: "#E5E7EB" },
        { x: 350, h: 75, color: "#E5E7EB" },
      ].map((bar, i) => (
        <rect
          key={i}
          x={bar.x}
          y={190 - bar.h}
          width="28"
          height={bar.h}
          rx="6"
          fill={bar.color}
        />
      ))}

      {/* ROI line */}
      <polyline
        points="44,155 84,140 124,160 164,135 204,145 244,65 284,130 324,125 364,140"
        stroke="#EF4444"
        strokeWidth="2"
        fill="none"
      />
      {[
        { x: 44, y: 155 }, { x: 84, y: 140 }, { x: 124, y: 160 },
        { x: 164, y: 135 }, { x: 204, y: 145 }, { x: 244, y: 65 },
        { x: 284, y: 130 }, { x: 324, y: 125 }, { x: 364, y: 140 },
      ].map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#EF4444" />
      ))}
    </svg>
  </div>
);

const ProblemSection = () => {
  return (
    <section className="py-20 md:py-28 bg-[#F5F5F5]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Heading */}
        <h2
          className="text-center text-[#1a1a2e] max-w-[700px] mx-auto mb-14 leading-tight"
          style={{
            fontSize: "clamp(30px, 4vw, 46px)",
            fontWeight: 800,
          }}
        >
          AI is changing work faster than people are being prepared for it
        </h2>

        {/* Staggered 2x2 Card Grid — asymmetric widths */}
        <div className="max-w-[1060px] mx-auto space-y-5">
          {/* Row 1: wider left, narrower right */}
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-5">
            {/* Card 1: Curriculum - WITH IMAGE */}
            <div className="group bg-white rounded-2xl border border-[#A7A2B4] overflow-hidden flex flex-col min-h-[500px] transition-all duration-300 ease-out hover:border-[#8B5CF6] hover:shadow-[0_4px_18px_rgba(139,92,246,0.10)]">
              <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-50 p-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/l69uf4zo_Design%201%201.png"
                  alt="AI Advancement Gap Chart"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-7">
                <h3 className="text-[20px] text-[#1a1a2e] leading-snug mb-2" style={{ fontWeight: 800 }}>
                  Curriculum needs to evolve as fast as AI
                </h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">
                  AI tools, models, and best practices shift every week. But the pace of change makes it difficult for educational institutions and L&D organisations to keep skilling and curriculum updated.
                </p>
              </div>
            </div>

            {/* Card 2: Faculty - WITH IMAGE */}
            <div className="group bg-white rounded-2xl border border-[#A7A2B4] overflow-hidden flex flex-col min-h-[500px] transition-all duration-300 ease-out hover:border-[#8B5CF6] hover:shadow-[0_4px_18px_rgba(139,92,246,0.10)]">
              <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-50 p-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/ewtox8nc_Frame%202147258447%20%287%29.png"
                  alt="Faculty and Practitioners"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-7">
                <h3 className="text-[20px] text-[#1a1a2e] leading-snug mb-2" style={{ fontWeight: 800 }}>
                  Institutions lack trained faculty and practitioners to teach applied AI
                </h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">
                  Teaching AI requires both academic delivery and practical industry context. Most institutions currently lack enough trained faculty and experienced practitioners to do this across departments.
                </p>
              </div>
            </div>
          </div>

          {/* Row 2: narrower left, wider right */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-5">
            {/* Card 3: Relevance - WITH IMAGE */}
            <div className="group bg-white rounded-2xl border border-[#A7A2B4] overflow-hidden flex flex-col min-h-[500px] transition-all duration-300 ease-out hover:border-[#8B5CF6] hover:shadow-[0_4px_18px_rgba(139,92,246,0.10)]">
              <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-50 p-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/cs9gj9d8_Frame%202147258447%20%285%29.png"
                  alt="AI Skills Discipline Alignment"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-7">
                <h3 className="text-[20px] text-[#1a1a2e] leading-snug mb-2" style={{ fontWeight: 800 }}>
                  Relevance is missing for disciplines, roles, and industries
                </h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">
                  Generic AI skilling is often too broad to translate into role- and industry-specific outcomes. Learners need to connect AI skills directly to their context and jobs.
                </p>
              </div>
            </div>

            {/* Card 4: Hands-on AI - WITH IMAGE */}
            <div className="group bg-white rounded-2xl border border-[#A7A2B4] overflow-hidden flex flex-col min-h-[500px] transition-all duration-300 ease-out hover:border-[#8B5CF6] hover:shadow-[0_4px_18px_rgba(139,92,246,0.10)]">
              <div className="flex-1 flex items-center justify-center overflow-hidden bg-gray-50 p-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/ip0t6awq_Frame%202147258447%20%286%29.png"
                  alt="Hands-on Learning Cost"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <div className="px-7 pb-7">
                <h3 className="text-[20px] text-[#1a1a2e] leading-snug mb-2" style={{ fontWeight: 800 }}>
                  Hands-on AI learning is hard and expensive to deliver at scale
                </h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">
                  Practical learning often needs lab environments, infrastructure, and classroom-ready workflows. That makes delivery expensive and operationally heavy across cohorts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
